import React, { useCallback, useEffect, useState } from 'react';
import api from '../../../services/api';

interface DistributionBucket {
  outcome: string;
  videoCount: number;
  averageScore: number;
  gradeDistribution: Record<string, number>;
}

interface RepOutcome {
  userId: string;
  name: string;
  totalVideos: number;
  closedVideos: number;
  avgScoreOverall: number;
  avgScoreClosed: number;
  lift: number;
}

const OUTCOME_COLORS: Record<string, string> = {
  CLOSED: 'bg-emerald-500 text-emerald-600 bg-emerald-50 dark:bg-emerald-950/30',
  APPOINTMENT_SET: 'bg-blue-500 text-blue-600 bg-blue-50 dark:bg-blue-950/30',
  LOST: 'bg-red-500 text-red-600 bg-red-50 dark:bg-red-950/30',
  PENDING: 'bg-slate-400 text-slate-500 bg-slate-100 dark:bg-slate-800',
};

const OutcomesTab: React.FC = () => {
  const [distribution, setDistribution] = useState<DistributionBucket[]>([]);
  const [repOutcomes, setRepOutcomes] = useState<RepOutcome[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [distRes, repRes] = await Promise.all([
        api.get('/reports/outcome-correlation'),
        api.get('/reports/rep-outcome'),
      ]);
      setDistribution(distRes.data.data ?? []);
      setRepOutcomes(repRes.data.data ?? []);
    } catch {
      setDistribution([]);
      setRepOutcomes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-3 border-primary border-t-transparent animate-spin rounded-full" />
      </div>
    );
  }

  const totalCorrelated = distribution.reduce((s, d) => s + d.videoCount, 0);
  const maxRepVideos = Math.max(1, ...repOutcomes.map((r) => r.totalVideos));

  return (
    <div className="space-y-6">
      {/* Score distribution - by outcome */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-8 shadow-sm">
        <h2 className="text-lg font-bold text-slate-900 dark:text-white uppercase tracking-widest mb-6">
          Score Distribution by Outcome
        </h2>
        {distribution.length === 0 ? (
          <div className="py-12 text-center">
            <span className="material-symbols-outlined text-[48px] text-slate-300 dark:text-slate-700">
              insights
            </span>
            <p className="text-sm text-slate-400 mt-2">
              No correlated videos yet. Ingest DMS records to see outcome correlation.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {distribution.map((d) => {
              const colorSet = OUTCOME_COLORS[d.outcome] ?? OUTCOME_COLORS.PENDING;
              const [dot] = colorSet.split(' ');
              const pct = totalCorrelated > 0 ? (d.videoCount / totalCorrelated) * 100 : 0;
              return (
                <div
                  key={d.outcome}
                  className="border border-slate-100 dark:border-slate-800 rounded-xl p-4"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <span className={`w-2 h-2 rounded-full ${dot}`} />
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                      {d.outcome}
                    </span>
                  </div>
                  <div className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight">
                    {d.averageScore.toFixed(1)}
                  </div>
                  <div className="text-[10px] font-bold text-slate-400 mt-1">
                    avg score across {d.videoCount} videos ({pct.toFixed(0)}%)
                  </div>
                  <div className="mt-3 flex gap-1">
                    {(['A', 'B', 'C', 'D', 'F'] as const).map((g) => (
                      <div key={g} className="flex-1 text-center">
                        <div className="text-[9px] font-black text-slate-400 mb-0.5">{g}</div>
                        <div className="text-xs font-bold text-slate-700 dark:text-slate-300">
                          {d.gradeDistribution[g] ?? 0}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Rep outcome table */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-800">
          <h2 className="text-lg font-bold text-slate-900 dark:text-white uppercase tracking-widest">
            Rep Outcome Lift
          </h2>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">
            Compares avg score on closed deals vs overall
          </p>
        </div>
        {repOutcomes.length === 0 ? (
          <p className="py-12 text-center text-sm text-slate-400">No rep outcome data yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/20">
                {['Rep', 'Videos', 'Closed', 'Close Rate', 'Avg Overall', 'Avg Closed', 'Lift'].map(
                  (h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-[10px] font-black uppercase tracking-widest text-slate-400"
                    >
                      {h}
                    </th>
                  ),
                )}
              </tr>
            </thead>
            <tbody>
              {repOutcomes.map((r) => {
                const closeRate = r.totalVideos > 0 ? (r.closedVideos / r.totalVideos) * 100 : 0;
                const liftClass =
                  r.lift > 0
                    ? 'text-emerald-600'
                    : r.lift < 0
                      ? 'text-red-500'
                      : 'text-slate-400';
                const volumePct = (r.totalVideos / maxRepVideos) * 100;
                return (
                  <tr
                    key={r.userId}
                    className="border-b border-slate-50 dark:border-slate-800/50 hover:bg-slate-50/50 dark:hover:bg-slate-800/20"
                  >
                    <td className="px-4 py-3 font-bold text-xs text-slate-900 dark:text-white">
                      {r.name}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="text-xs">{r.totalVideos}</span>
                        <div className="flex-1 max-w-[80px] h-1 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-slate-400"
                            style={{ width: `${volumePct}%` }}
                          />
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs">{r.closedVideos}</td>
                    <td className="px-4 py-3 text-xs text-emerald-600 font-bold">
                      {closeRate.toFixed(0)}%
                    </td>
                    <td className="px-4 py-3 text-xs">{r.avgScoreOverall.toFixed(1)}</td>
                    <td className="px-4 py-3 text-xs font-bold text-emerald-600">
                      {r.avgScoreClosed > 0 ? r.avgScoreClosed.toFixed(1) : '—'}
                    </td>
                    <td className={`px-4 py-3 text-xs font-black ${liftClass}`}>
                      {r.lift > 0 ? '+' : ''}
                      {r.avgScoreClosed > 0 ? r.lift.toFixed(1) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default OutcomesTab;
